import { useLazyQuery } from '@apollo/client'
import { isEmpty } from 'lodash'
import { useState, ReactElement } from 'react'
import { useIntl, defineMessages } from 'react-intl'
import { Autocomplete, useDebouncedQuery } from 'components/v2/Autocomplete'
import { IInputProps } from 'components/Input'
import { GTBusinessCustomerSuggestion, GTBusinessCustomerSuggestionsResponse } from 'gql/types.generated'
import { GET_BUSINESS_CUSTOMER_SUGGESTIONS } from 'features/BusinessCustomer/queries-legacy'

const messages = defineMessages({
  error: {
    id: 'businessCustomer.autocomplete.error',
    defaultMessage: 'There was an issue while loading your business customers'
  },
  empty: {
    id: 'businessCustomer.autocomplete.empty',
    defaultMessage: 'No business customers found'
  }
})

interface IBusinessCustomerAutocompleteProps extends Omit<IInputProps<HTMLInputElement>, 'onChange'> {
  onChange: (businessCustomers: GTBusinessCustomerSuggestion[]) => void
  uniqueName?: boolean
}

const getShownItems = (
  items: GTBusinessCustomerSuggestion[] | undefined,
  uniqueName: boolean
): GTBusinessCustomerSuggestion[] => {
  if (!uniqueName) {
    return items || []
  }

  const normalizedKeysMap: Record<string, GTBusinessCustomerSuggestion> = {}

  return (
    items?.reduce((result: GTBusinessCustomerSuggestion[], businessCustomer): GTBusinessCustomerSuggestion[] => {
      if (isEmpty(normalizedKeysMap[businessCustomer.name])) {
        normalizedKeysMap[businessCustomer.name] = businessCustomer
        result.push(businessCustomer)
      }
      return result
    }, []) || []
  )
}

export const BusinessCustomerAutocomplete = ({
  onChange,
  uniqueName = false,
  ...props
}: IBusinessCustomerAutocompleteProps): ReactElement => {
  const intl = useIntl()
  const [pattern, setPattern] = useState('')
  const [getBusinessCustomers, { data, loading, error }] = useLazyQuery<{
    businessCustomerSuggestions: GTBusinessCustomerSuggestionsResponse
  }>(GET_BUSINESS_CUSTOMER_SUGGESTIONS)
  const { isDebouncing, shouldShowItems } = useDebouncedQuery({
    query: getBusinessCustomers,
    pattern
  })

  const shownItems = getShownItems(data?.businessCustomerSuggestions.items, uniqueName)

  const handleChange = (businessCustomer: GTBusinessCustomerSuggestion, { inputValue = '' } = {}): void => {
    setPattern(inputValue)
    if (!isEmpty(businessCustomer)) {
      const businessCustomers =
        data?.businessCustomerSuggestions.items.filter((item) => item.name === businessCustomer.name) || []
      onChange(businessCustomers)
    } else {
      onChange([])
    }
  }

  return (
    <Autocomplete
      {...props}
      items={shownItems}
      getItemLabel={(businessCustomer: GTBusinessCustomerSuggestion): string => businessCustomer.name}
      loading={loading || isDebouncing}
      error={error}
      errorMessage={intl.formatMessage(messages.error)}
      emptyMessage={intl.formatMessage(messages.empty)}
      onChange={handleChange}
      shouldShowItems={shouldShowItems}
    />
  )
}
